import { TextFieldStyled } from "../../shared/styles";
import { Box } from "@mui/material";
import { ModalV2 } from "../../shared/modal/modal";
import { useEffect, useState } from "react";
import {
  createContactAgenda,
  editContactAgenda,
} from "../../redux/actions/agenda.actions";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { Dropdown } from "../../shared/dropdown/dropdown";
import { networks } from "../mockedData";
import { getCoinsByNetwork } from "../home/single-payment.utils";

// Estado inicial de un contacto nuevo. Vive fuera del componente porque se usa
// en tres lugares y tenerlo duplicado fue como se olvidaron campos antes.
const EMPTY_CONTACT = {
  nombre: "",
  apellido: "",
  email: "",
  walletadress: "",
  alias: "",
  chain: "",
  token: "",
};

export const ModalCreateEditContact = ({
  open,
  handleClose,
  idContact = null,
  contactData,
  isEdit = false,
  userId,
}) => {
  const [contact, setContact] = useState(contactData || EMPTY_CONTACT);

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  // Red y token son obligatorios: son lo que despues permite fijar la moneda al
  // pagar. Los contactos viejos estan sin declarar y el modal de edicion pide
  // completarlos, que es como se va cerrando ese hueco.
  const isFormIncomplete =
    !contact.nombre ||
    !contact.email ||
    !contact.walletadress ||
    !contact.alias ||
    !contact.chain ||
    !contact.token ||
    !isValidEmail(contact.email);

  // Sin red elegida no hay tokens que ofrecer. getCoinsByNetwork devuelve la
  // lista completa cuando no reconoce la red, y eso dejaria elegir TRX en
  // Polygon.
  const tokenOptions = contact.chain ? getCoinsByNetwork(contact.chain) : [];

  const dispatch = useDispatch();

  function handleChangeContact(e) {
    setContact({ ...contact, [e.target.name]: e.target.value });
  }

  const [isSaving, setIsSaving] = useState(false);

  async function handleCreateEditContact() {
    if (isSaving) return;
    setIsSaving(true);

    const result = isEdit && idContact
      ? await dispatch(editContactAgenda(idContact, contact, userId))
      : await dispatch(createContactAgenda(contact, userId));

    setIsSaving(false);

    // Solo se cierra el modal si salio bien. Si fallo, se queda abierto con el
    // toast de error para poder reintentar sin recargar los campos.
    if (result?.success) {
      toast.success(isEdit ? "Contact updated" : "Contact created");
      if (!isEdit) {
        setContact(EMPTY_CONTACT);
      }
      handleClose(isEdit ? "editContact" : "createContact");
    } else {
      toast.error(
        result?.message ||
          (isEdit ? "Could not update contact" : "Could not create contact")
      );
    }
  }

  useEffect(() => {
    if (isEdit) {
      // Un contacto viejo llega con chain/token en null. Se normalizan a "" para
      // que los Dropdown queden vacios en vez de romperse.
      setContact({
        ...contactData,
        chain: contactData?.chain || "",
        token: contactData?.token || "",
      });
    } else {
      setContact(EMPTY_CONTACT);
    }
  }, [isEdit, contactData]);

  return (
    <ModalV2
      isOpen={open}
      title={isEdit ? "Edit contact" : "Create contact"}
      handleClose={() => handleClose(isEdit ? "editContact" : "createContact")}
      actionButtons={[
        {
          label: "Cancel",
          onClick: () => handleClose(isEdit ? "editContact" : "createContact"),
        },
        {
          label: isEdit ? "Edit" : "Create",
          onClick: handleCreateEditContact,
          disabled: isFormIncomplete || isSaving,
          tooltipLabel: isFormIncomplete
            ? isValidEmail(contact.email) || !contact?.email
              ? "Fill required fields"
              : "Invalid email format"
            : "",
        },
      ]}
    >
      <Box>
        <Box
          display={"flex"}
          flexDirection={"column"}
          gap={"20px"}
          mb={"24px"}
          px={"24px"}
        >
          <TextFieldStyled
            required
            name={"alias"}
            value={contact.alias}
            label={"Alias"}
            type={"text"}
            InputProps={{
              disableUnderline: true,
            }}
            onChange={handleChangeContact}
          />
          <TextFieldStyled
            required
            name={"nombre"}
            value={contact.nombre}
            label={"Name"}
            type={"text"}
            InputProps={{
              disableUnderline: true,
            }}
            onChange={handleChangeContact}
          />
          <TextFieldStyled
            name={"apellido"}
            value={contact.apellido}
            label={"Last name"}
            type={"text"}
            InputProps={{
              disableUnderline: true,
            }}
            onChange={handleChangeContact}
          />
          <TextFieldStyled
            required
            name={"email"}
            value={contact.email}
            label={"Email"}
            type={"email"}
            InputProps={{
              disableUnderline: true,
            }}
            onChange={handleChangeContact}
          />
          <TextFieldStyled
            required
            name={"walletadress"}
            value={contact.walletadress}
            label={"Wallet address"}
            type={"text"}
            InputProps={{
              disableUnderline: true,
            }}
            onChange={handleChangeContact}
          />
          {/*
            Red y token del destinatario. Es lo que despues fija la moneda en el
            pago: sin esto el portal deja mandar cualquier token a cualquier
            direccion, que es como se enviaron 1000 USDT a una wallet de USDC.
          */}
          <Dropdown
            required
            label={"Network"}
            options={networks}
            defaultValue={contact.chain}
            withSearch={false}
            onSelect={(chain) =>
              setContact((prev) => ({
                ...prev,
                chain,
                // El token viejo puede no existir en la red nueva (TRX no esta
                // en Polygon): se limpia y se vuelve a elegir.
                token: getCoinsByNetwork(chain).some((c) => c.value === prev.token)
                  ? prev.token
                  : "",
              }))
            }
          />
          <Dropdown
            required
            label={"Token"}
            options={tokenOptions}
            defaultValue={contact.token}
            withSearch={false}
            onSelect={(token) => setContact((prev) => ({ ...prev, token }))}
          />
        </Box>
      </Box>
    </ModalV2>
  );
};
